import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BecomeSeller = () => {
  const ADMIN_DASHBOARD_URL = process.env.NODE_ENV === "production"
    ? "https://admin.pikkodubai.com"
    : "http://localhost:3001";

  const sellerRegisterUrl = `${ADMIN_DASHBOARD_URL}/seller-auth`;

  const benefits = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Reach Thousands of Customers',
      desc: 'Access a growing base of buyers actively shopping across Dubai and the UAE.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Easy & Fast Payouts',
      desc: 'Get paid reliably with transparent commission structures and timely transfers.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'Powerful Seller Dashboard',
      desc: 'Manage your inventory, orders, and analytics all from one intuitive panel.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Trusted & Secure Platform',
      desc: 'Sell with confidence on a verified marketplace with buyer and seller protection.',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Create Your Seller Account',
      desc: 'Register on our seller portal, fill in your business details, and set up your store profile.',
    },
    {
      number: '02',
      title: 'Upload Required Documents',
      desc: 'Submit your trade license, tax registration (TRN), and valid Emirates ID for verification.',
    },
    {
      number: '03',
      title: 'Get Approved & Start Selling',
      desc: 'Once verified by our team, list your products and start reaching customers immediately.',
    },
  ];

  const requirements = [
    'Valid UAE Trade License',
    'Tax Registration Number (TRN)',
    'Emirates ID or Passport Copy',
    'Active Bank Account in UAE',
    'Product photos and descriptions',
    'Contact email and phone number',
  ];

  return (
    <div className="bg-[#f5f5f5]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white text-gray-900 border-b border-gray-100">
        {/* Background glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#E21C1B]/6 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E21C1B]/4 blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-12 items-center py-20 md:py-14 sm:py-12">

            {/* Left: Text */}
            <div>
              <span className="inline-block bg-[#E21C1B]/10 border border-[#E21C1B]/20 text-[#E21C1B] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                Seller Program — Open Now
              </span>
              <h1 className="text-5xl md:text-4xl sm:text-3xl font-extrabold mb-5 leading-tight text-gray-900">
                Grow Your Business on<br />
                <span className="text-[#E21C1B]">Pikko Dubai</span>
              </h1>
              <p className="text-gray-500 text-lg sm:text-base max-w-xl mb-8 leading-relaxed">
                Join hundreds of verified sellers across the UAE. List your products, reach thousands of buyers, and scale your business — all from one platform.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href={sellerRegisterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3.5 bg-[#E21C1B] text-white rounded-lg font-bold text-base hover:bg-[#c01717] transition-all duration-200 shadow-lg shadow-red-200"
                >
                  Apply as Seller
                </a>
                <a
                  href="#how-it-works"
                  className="inline-block px-8 py-3.5 border border-gray-300 text-gray-700 rounded-lg font-semibold text-base hover:border-[#E21C1B] hover:text-[#E21C1B] transition-all duration-200"
                >
                  See How It Works
                </a>
              </div>
              {/* Stats row */}
              <div className="flex items-center gap-8 sm:gap-3">
                {[
                  { value: '10,000+', label: 'Active Buyers' },
                  { value: '500+', label: 'Verified Sellers' },
                  { value: '50,000+', label: 'Products Listed' },
                ].map((stat, i, arr) => (
                  <React.Fragment key={stat.label}>
                    <div>
                      <div className="text-xl xs:text-lg font-extrabold text-gray-900">{stat.value}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                    </div>
                    {i < arr.length - 1 && <div className="w-px h-8 bg-gray-200 flex-shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Right: B2B Illustration */}
            <div className="flex justify-center items-center md:hidden">
              <div className="relative w-full max-w-[460px]">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-72 h-72 rounded-full bg-[#E21C1B]/20 blur-3xl" />
                </div>
                <svg viewBox="0 0 480 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10 w-full">
                  {/* Decorative rings */}
                  <circle cx="240" cy="185" r="155" stroke="rgba(226,28,27,0.08)" strokeWidth="1" strokeDasharray="8 5"/>
                  <circle cx="240" cy="185" r="115" stroke="rgba(226,28,27,0.10)" strokeWidth="1"/>

                  {/* LEFT PERSON */}
                  <circle cx="108" cy="85" r="28" fill="#475569"/>
                  <ellipse cx="108" cy="79" rx="19" ry="21" fill="#CBD5E1"/>
                  <path d="M73 262 L73 170 C73 156 90 146 108 146 C126 146 143 156 143 170 L143 262 Z" fill="#334155"/>
                  <path d="M97 146 L108 166 L119 146" fill="white" opacity="0.85"/>
                  <path d="M105 163 L108 220 L111 163 L108 158 Z" fill="#E21C1B"/>
                  <path d="M143 178 Q168 183 192 188" stroke="#CBD5E1" strokeWidth="16" strokeLinecap="round"/>
                  <path d="M192 188 Q210 191 222 192" stroke="#CBD5E1" strokeWidth="13" strokeLinecap="round"/>

                  {/* RIGHT PERSON */}
                  <circle cx="372" cy="85" r="28" fill="#475569"/>
                  <ellipse cx="372" cy="79" rx="19" ry="21" fill="#D4A574"/>
                  <path d="M337 262 L337 170 C337 156 354 146 372 146 C390 146 407 156 407 170 L407 262 Z" fill="#1e3a5f"/>
                  <path d="M361 146 L372 166 L383 146" fill="white" opacity="0.85"/>
                  <path d="M369 163 L372 220 L375 163 L372 158 Z" fill="#3B82F6"/>
                  <path d="M337 178 Q312 183 288 188" stroke="#D4A574" strokeWidth="16" strokeLinecap="round"/>
                  <path d="M288 188 Q270 191 258 192" stroke="#D4A574" strokeWidth="13" strokeLinecap="round"/>

                  {/* HANDSHAKE GLOW + CLASPED HANDS */}
                  <ellipse cx="240" cy="191" rx="34" ry="23" fill="rgba(226,28,27,0.22)"/>
                  <ellipse cx="240" cy="191" rx="21" ry="14" fill="rgba(226,28,27,0.42)"/>
                  <path d="M216 186 Q240 200 264 186" stroke="#E2E8F0" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                  <path d="M218 194 Q240 206 262 194" stroke="#D4A574" strokeWidth="5" strokeLinecap="round" fill="none"/>
                  <circle cx="240" cy="192" r="12" fill="#E21C1B"/>
                  <path d="M233 189 L237 196 L247 188" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

                  {/* FLOATING: Bar Chart card */}
                  <rect x="188" y="14" width="104" height="60" rx="10" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
                  <rect x="200" y="46" width="12" height="20" rx="2" fill="rgba(226,28,27,0.65)"/>
                  <rect x="217" y="36" width="12" height="30" rx="2" fill="rgba(226,28,27,0.82)"/>
                  <rect x="234" y="27" width="12" height="39" rx="2" fill="#E21C1B"/>
                  <rect x="251" y="32" width="12" height="34" rx="2" fill="rgba(226,28,27,0.82)"/>
                  <rect x="268" y="40" width="12" height="26" rx="2" fill="rgba(226,28,27,0.65)"/>
                  <path d="M200 42 Q218 31 234 24 Q253 30 272 39" stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" fill="none"/>
                  <circle cx="272" cy="39" r="3.5" fill="#FBBF24"/>

                  {/* FLOATING: Contract / document */}
                  <rect x="26" y="236" width="56" height="70" rx="7" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
                  <path d="M37 256 L72 256" stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M37 265 L72 265" stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M37 274 L57 274" stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="54" cy="292" r="11" fill="#34D399" opacity="0.88"/>
                  <path d="M48 292 L53 297 L61 287" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>

                  {/* FLOATING: Building */}
                  <rect x="392" y="230" width="60" height="74" rx="4" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
                  <rect x="399" y="238" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="416" y="238" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="433" y="238" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="399" y="255" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="416" y="255" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="433" y="255" width="12" height="12" rx="2" fill="#60A5FA" opacity="0.5"/>
                  <rect x="410" y="272" width="24" height="32" rx="3" fill="#93C5FD" opacity="0.3"/>
                  <path d="M385 230 L422 215 L459 230" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>

                  {/* Sparkle dots */}
                  <circle cx="158" cy="40" r="5" fill="#FBBF24" opacity="0.6"/>
                  <circle cx="156" cy="38" r="2" fill="white" opacity="0.75"/>
                  <circle cx="326" cy="30" r="5" fill="#60A5FA" opacity="0.6"/>
                  <circle cx="324" cy="28" r="2" fill="white" opacity="0.75"/>
                  <circle cx="70" cy="218" r="4" fill="#34D399" opacity="0.5"/>
                  <circle cx="414" cy="210" r="4" fill="#F472B6" opacity="0.5"/>

                  {/* Dashed arc connecting the two people */}
                  <path d="M136 102 Q240 40 344 102" stroke="rgba(226,28,27,0.16)" strokeWidth="1.5" strokeDasharray="5 4" fill="none"/>
                </svg>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Why Sell Here */}
      <section className="py-16">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-2xl font-bold text-gray-900 mb-3">Why Sell on Pikko Dubai?</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Everything you need to run a successful online store in the UAE, built into one platform.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 sm:grid-cols-1 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex gap-5 items-start hover:shadow-md transition-all duration-200">
                <div className="flex-shrink-0 w-14 h-14 bg-[#E21C1B]/10 text-[#E21C1B] rounded-xl flex items-center justify-center">
                  {b.icon}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-2xl font-bold text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Getting started is simple. Follow these three steps and you'll be selling in no time.</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-3 sm:grid-cols-1 gap-8 relative">
            {/* Connector line — desktop only */}
            <div className="absolute top-10 left-[calc(16.66%+16px)] right-[calc(16.66%+16px)] h-0.5 bg-[#E21C1B]/20 hidden md:block sm:hidden" />
            {steps.map((step, i) => (
              <div key={step.number} className="relative text-center flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-[#E21C1B] text-white text-2xl font-extrabold flex items-center justify-center mb-5 shadow-lg z-10">
                  {step.number}
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <div className="sm:block hidden mt-6 w-0.5 h-8 bg-[#E21C1B]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-1 sm:grid-cols-1 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-2xl font-bold text-gray-900 mb-4">What You'll Need</h2>
              <p className="text-gray-500 mb-6 leading-relaxed">
                To keep our marketplace trusted and safe, we verify all sellers before they go live. Make sure you have these documents ready before applying.
              </p>
              <ul className="space-y-3">
                {requirements.map((req) => (
                  <li key={req} className="flex items-center gap-3 text-gray-700">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#E21C1B] flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="font-bold text-xl text-gray-900 mb-2">Ready to Join?</h3>
              <p className="text-gray-500 mb-6 text-sm leading-relaxed">
                Create your seller account today and get access to thousands of buyers across the UAE. Our team typically reviews applications within 1–3 business days.
              </p>
              <a
                href={sellerRegisterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-3.5 bg-[#E21C1B] text-white rounded-md font-bold hover:bg-[#c01717] transition-all duration-200 mb-3"
              >
                Apply as Seller
              </a>
              <p className="text-center text-gray-400 text-xs">
                Already have an account?{' '}
                <a href={sellerRegisterUrl} target="_blank" rel="noopener noreferrer" className="text-[#E21C1B] font-medium hover:underline">
                  Sign in to your seller portal
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-16 bg-[#1a1a1a] text-white">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto text-center">
          <h2 className="text-3xl sm:text-2xl font-bold mb-4">Start Selling on Pikko Dubai Today</h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            Join a growing community of UAE sellers. No hidden fees, no complicated setup — just you and your products in front of the right customers.
          </p>
          <a
            href={sellerRegisterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-[#E21C1B] text-white rounded-md font-bold text-base hover:bg-[#c01717] transition-all duration-200"
          >
            Get Started — It's Free
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeSeller;
